import { useEffect } from 'react';
import { useStore } from 'zustand';
import { useAnnotationStore } from './annotationStore';
import { SVGAnnotationRenderer } from './SVGAnnotationRenderer';
import { useCreateAnnotationMutation, useDeleteAnnotationMutation } from './useAnnotationHooks';
import { useUnityBuildStore } from '../../stores/store';

interface PastePreviewProps {}

export const PastePreview: React.FC<PastePreviewProps> = () => {
  const {
    isPastePreviewActive,
    pastePreviewAnnotation,
    setPastePreview,
    cutOriginalAnnotation,
    confirmCut,
    cancelCut,
    isDimensionalMode,
  } = useAnnotationStore();
  const createAnnotationMutation = useCreateAnnotationMutation();
  const deleteAnnotationMutation = useDeleteAnnotationMutation();
  const { selectedObject, cursorPosition } = useStore(useUnityBuildStore);

  // Handle click and key events for paste preview
  useEffect(() => {
    if (!isPastePreviewActive) return;

    const handleClick = (e: MouseEvent) => {
      if (isPastePreviewActive && pastePreviewAnnotation && selectedObject?.id && cursorPosition) {
        // Create the annotation data with proper geometry positioning
        const annotationData = {
          id: `annotation-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          objectid: selectedObject.id,
          annotationdata: {
            id: `annotation-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            type: pastePreviewAnnotation.type,
            geometry: {
              ...pastePreviewAnnotation.geometry,
              // Handle different annotation types with appropriate positioning
              ...(pastePreviewAnnotation.type === 2 || pastePreviewAnnotation.type === 3
                ? {
                    // AnnotationType.CIRCLE = 2, ELLIPSE = 3
                    cx: cursorPosition.x.toString(),
                    cy: cursorPosition.y.toString(),
                  }
                : pastePreviewAnnotation.type === 4 || pastePreviewAnnotation.type === 9
                  ? {
                      // LINE = 4, ARROW = 9 - preserve original direction, just move to new position
                      x1: cursorPosition.x.toString(),
                      y1: cursorPosition.y.toString(),
                      x2: (
                        cursorPosition.x +
                        (parseFloat(pastePreviewAnnotation.geometry?.x2 || '0') -
                          parseFloat(pastePreviewAnnotation.geometry?.x1 || '0'))
                      ).toString(),
                      y2: (
                        cursorPosition.y +
                        (parseFloat(pastePreviewAnnotation.geometry?.y2 || '0') -
                          parseFloat(pastePreviewAnnotation.geometry?.y1 || '0'))
                      ).toString(),
                    }
                  : pastePreviewAnnotation.type === 5
                    ? {
                        // POLYLINE = 5 - preserve original shape, just move to new position
                        points: (pastePreviewAnnotation.geometry?.points || '')
                          .split(' ')
                          .map((point: string) => {
                            const [px, py] = point.split(',').map(Number);
                            const originalX = parseFloat(pastePreviewAnnotation.geometry?.x || '0');
                            const originalY = parseFloat(pastePreviewAnnotation.geometry?.y || '0');
                            const offsetX = cursorPosition.x - originalX;
                            const offsetY = cursorPosition.y - originalY;
                            return `${px + offsetX},${py + offsetY}`;
                          })
                          .join(' '),
                        // Also update bounding box for compatibility
                        x: cursorPosition.x.toString(),
                        y: cursorPosition.y.toString(),
                      }
                    : {
                        x: cursorPosition.x.toString(),
                        y: cursorPosition.y.toString(),
                      }),
            },
            style: pastePreviewAnnotation.style,
            isdimensional:
              pastePreviewAnnotation.type === 0 || pastePreviewAnnotation.type === 7
                ? false
                : isDimensionalMode, // TEXT = 0, HIGHLIGHTER = 7, always non-dimensional
          },
        };

        // Create the annotation via the mutation
        createAnnotationMutation.mutate(annotationData);

        // If this was a cut operation, delete the original from the database
        if (cutOriginalAnnotation && selectedObject?.id) {
          deleteAnnotationMutation.mutate({
            id: cutOriginalAnnotation.id,
            objectId: selectedObject.id,
          });
          confirmCut();
        }

        // Clear the paste preview
        setPastePreview(null, false);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Cancel paste preview and restore cut item if applicable
        if (cutOriginalAnnotation) {
          cancelCut();
        } else {
          setPastePreview(null, false);
        }
      }
    };

    // Use capture phase to ensure we get the events before they're blocked
    document.addEventListener('click', handleClick, true);
    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      document.removeEventListener('click', handleClick, true);
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [
    cancelCut,
    confirmCut,
    cutOriginalAnnotation,
    deleteAnnotationMutation,
    isPastePreviewActive,
    pastePreviewAnnotation,
    cursorPosition,
    setPastePreview,
    createAnnotationMutation,
    selectedObject,
    isDimensionalMode,
  ]);

  // Add a more reliable click handler by adding a transparent overlay
  const handleOverlayClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isPastePreviewActive && pastePreviewAnnotation && selectedObject?.id && cursorPosition) {
      // Create the annotation data with proper geometry positioning
      const annotationData = {
        id: `annotation-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        objectid: selectedObject.id,
        annotationdata: {
          id: `annotation-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          type: pastePreviewAnnotation.type,
          geometry: {
            ...pastePreviewAnnotation.geometry,
            // Handle different annotation types with appropriate positioning
            ...(pastePreviewAnnotation.type === 2 || pastePreviewAnnotation.type === 3
              ? {
                  // AnnotationType.CIRCLE = 2, ELLIPSE = 3
                  cx: cursorPosition.x.toString(),
                  cy: cursorPosition.y.toString(),
                }
              : pastePreviewAnnotation.type === 4 || pastePreviewAnnotation.type === 9
                ? {
                    // LINE = 4, ARROW = 9 - preserve original direction, just move to new position
                    x1: cursorPosition.x.toString(),
                    y1: cursorPosition.y.toString(),
                    x2: (
                      cursorPosition.x +
                      (parseFloat(pastePreviewAnnotation.geometry?.x2 || '0') -
                        parseFloat(pastePreviewAnnotation.geometry?.x1 || '0'))
                    ).toString(),
                    y2: (
                      cursorPosition.y +
                      (parseFloat(pastePreviewAnnotation.geometry?.y2 || '0') -
                        parseFloat(pastePreviewAnnotation.geometry?.y1 || '0'))
                    ).toString(),
                  }
                : pastePreviewAnnotation.type === 5
                  ? {
                      // POLYLINE = 5 - preserve original shape, just move to new position
                      points: (pastePreviewAnnotation.geometry?.points || '')
                        .split(' ')
                        .map((point: string) => {
                          const [px, py] = point.split(',').map(Number);
                          const originalX = parseFloat(pastePreviewAnnotation.geometry?.x || '0');
                          const originalY = parseFloat(pastePreviewAnnotation.geometry?.y || '0');
                          const offsetX = cursorPosition.x - originalX;
                          const offsetY = cursorPosition.y - originalY;
                          return `${px + offsetX},${py + offsetY}`;
                        })
                        .join(' '),
                      // Also update bounding box for compatibility
                      x: cursorPosition.x.toString(),
                      y: cursorPosition.y.toString(),
                    }
                  : {
                      x: cursorPosition.x.toString(),
                      y: cursorPosition.y.toString(),
                    }),
          },
          style: pastePreviewAnnotation.style,
          isdimensional:
            pastePreviewAnnotation.type === 0 || pastePreviewAnnotation.type === 7
              ? false
              : isDimensionalMode, // TEXT = 0, HIGHLIGHTER = 7, always non-dimensional
        },
      };

      // Create the annotation via the mutation
      createAnnotationMutation.mutate(annotationData);

      // If this was a cut operation, delete the original from the database
      if (cutOriginalAnnotation && selectedObject?.id) {
        deleteAnnotationMutation.mutate({
          id: cutOriginalAnnotation.id,
          objectId: selectedObject.id,
        });
        confirmCut();
      }

      // Clear the paste preview
      setPastePreview(null, false);
    }
  };

  if (!isPastePreviewActive || !pastePreviewAnnotation) {
    return null;
  }

  // Safety check for cursor position
  if (
    !cursorPosition ||
    typeof cursorPosition.x !== 'number' ||
    typeof cursorPosition.y !== 'number'
  ) {
    return null;
  }

  // Create a preview annotation with updated position
  const previewAnnotation = {
    ...pastePreviewAnnotation,
    geometry: {
      ...pastePreviewAnnotation.geometry,
      // Handle different annotation types with appropriate positioning
      ...(pastePreviewAnnotation.type === 2 || pastePreviewAnnotation.type === 3
        ? {
            // AnnotationType.CIRCLE = 2, ELLIPSE = 3
            cx: cursorPosition.x.toString(),
            cy: cursorPosition.y.toString(),
          }
        : pastePreviewAnnotation.type === 4 || pastePreviewAnnotation.type === 9
          ? {
              // LINE = 4, ARROW = 9 - preserve original direction, just move to new position
              x1: cursorPosition?.x?.toString() || '0',
              y1: cursorPosition?.y?.toString() || '0',
              x2: (
                (cursorPosition?.x || 0) +
                (parseFloat(pastePreviewAnnotation.geometry?.x2 || '0') -
                  parseFloat(pastePreviewAnnotation.geometry?.x1 || '0'))
              ).toString(),
              y2: (
                (cursorPosition?.y || 0) +
                (parseFloat(pastePreviewAnnotation.geometry?.y2 || '0') -
                  parseFloat(pastePreviewAnnotation.geometry?.y1 || '0'))
              ).toString(),
            }
          : pastePreviewAnnotation.type === 5
            ? {
                // POLYLINE = 5 - preserve original shape, just move to new position
                points: (pastePreviewAnnotation.geometry?.points || '')
                  .split(' ')
                  .map((point: string) => {
                    const [px, py] = point.split(',').map(Number);
                    const originalX = parseFloat(pastePreviewAnnotation.geometry?.x || '0');
                    const originalY = parseFloat(pastePreviewAnnotation.geometry?.y || '0');
                    const offsetX = (cursorPosition?.x || 0) - originalX;
                    const offsetY = (cursorPosition?.y || 0) - originalY;
                    return `${px + offsetX},${py + offsetY}`;
                  })
                  .join(' '),
                // Also update bounding box for compatibility
                x: cursorPosition?.x?.toString() || '0',
                y: cursorPosition?.y?.toString() || '0',
              }
            : {
                x: cursorPosition?.x?.toString() || '0',
                y: cursorPosition?.y?.toString() || '0',
              }),
    },
    // Make it semi-transparent to indicate it's a preview
    style: {
      ...pastePreviewAnnotation.style,
      opacity: '0.6',
    },
  };

  // Render the preview annotation directly using SVGAnnotationRenderer
  // This will be positioned within the SVG coordinate system
  return (
    <>
      {/* Transparent overlay to capture clicks reliably */}
      <rect
        x="0"
        y="0"
        width="100%"
        height="100%"
        fill="transparent"
        style={{ pointerEvents: 'all', cursor: 'crosshair' }}
        onClick={handleOverlayClick}
      />

      {/* Preview annotation */}
      <SVGAnnotationRenderer
        annotation={previewAnnotation}
        onHyperlinkClick={() => {}}
        onSelect={() => {}}
        isSelected={false}
        getTargetObjectName={() => 'Preview'}
      />
    </>
  );
};
